// Which mirror makes the card for the links I send, per site (#578, shared/src/linkPreviews.ts). Saved to the account
// (`linkPreviews` in /api/me/settings); the room reads it when it makes the card. Held for this page only: no
// localStorage, because nothing on this device uses the choice, and the list of hosts comes from the server.
import { useSyncExternalStore } from "react";
import { PREVIEW_AUTO, type LinkPreviewChoices, type LinkPreviewList, type PreviewSite } from "@bloom/shared";
import { api } from "./api.ts";

const listeners = new Set<() => void>();
let choices: LinkPreviewChoices = {};
let picks = 0;

/** How many picks were made on this device. The session compares it before and after /api/me, so a late answer cannot undo a pick. */
export const linkPreviewPicks = () => picks;

/** Sets one site on this device right away; `save` also stores it on the account. The PUT names only that site. */
export function setLinkPreview(site: PreviewSite, choice: string, { save = true } = {}) {
  const next = { ...choices };
  if (choice === PREVIEW_AUTO) delete next[site];
  else next[site] = choice;
  choices = next;
  if (save) picks++;
  for (const fn of listeners) fn();
  if (save) void api("/me/settings", { method: "PUT", body: { linkPreviews: { [site]: choice } } }).catch(() => {});
}

/** Takes the account's choices (from /api/me), unless I picked one here since the request left. */
export function takeAccountLinkPreviews(account: LinkPreviewChoices | undefined, picksBefore: number) {
  if (!account || picksBefore !== picks) return;
  if (JSON.stringify(account) === JSON.stringify(choices)) return;
  choices = { ...account };
  for (const fn of listeners) fn();
}

export const getLinkPreviews = () => choices;

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useLinkPreviews = (): LinkPreviewChoices => useSyncExternalStore(subscribe, () => choices);

/** GET /api/link-previews: the sites and the hosts the server accepts. Null when it fails (an old server, offline): the section hides. */
export async function loadLinkPreviewList(): Promise<LinkPreviewList | null> {
  try {
    const list = await api<LinkPreviewList>("/link-previews");
    return Array.isArray(list?.sites) && list.sites.length ? list : null;
  } catch {
    return null;
  }
}
